/**
 * Captura e guarda a origem do visitante (Google Ads / UTM / referência)
 * para: (1) atribuir conversões, (2) mostrar ao atendente de onde a pessoa veio
 * quando ela abre o WhatsApp/Huggy.
 *
 * - first-touch: gravado uma única vez (primeira visita da pessoa)
 * - last-touch: sobrescrito sempre que chega com parâmetros novos
 * - sid: código curto e estável por navegador, incluído na mensagem do WhatsApp
 */

const K_FIRST = "ci_attr_first";
const K_LAST = "ci_attr_last";
const K_SID = "ci_sid";

const PARAM_KEYS = [
  "gclid",
  "wbraid",
  "gbraid",
  "gad_source",
  "gclsrc",
  "msclkid",
  "fbclid",
  "ttclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
] as const;

export type AttributionTouch = {
  ts: string;
  params: Record<string, string>;
  referrer: string;
  landing: string;
};

type Store = { first?: AttributionTouch; last?: AttributionTouch; sid: string };

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* modo privado / storage bloqueado */
  }
}

function parse(raw: string | null): AttributionTouch | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as AttributionTouch;
  } catch {
    return undefined;
  }
}

function makeSid(): string {
  const rnd =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return rnd;
}

export function getSid(): string {
  let sid = safeGet(K_SID);
  if (!sid) {
    sid = makeSid();
    safeSet(K_SID, sid);
  }
  return sid;
}

/** Roda uma vez no carregamento. Lê a URL atual e persiste a origem. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  getSid();

  const url = new URL(window.location.href);
  const params: Record<string, string> = {};
  for (const key of PARAM_KEYS) {
    const v = url.searchParams.get(key);
    if (v) params[key] = v;
  }
  const hasParams = Object.keys(params).length > 0;

  const referrer = document.referrer || "";
  const sameHost = (() => {
    try {
      return referrer && new URL(referrer).host === window.location.host;
    } catch {
      return false;
    }
  })();

  // Sem parâmetros e navegação interna: não mexe em nada.
  if (!hasParams && (sameHost || !referrer) && safeGet(K_FIRST)) return;

  const touch: AttributionTouch = {
    ts: new Date().toISOString(),
    params,
    referrer: sameHost ? "" : referrer,
    landing: url.pathname + url.search,
  };

  if (!safeGet(K_FIRST)) safeSet(K_FIRST, JSON.stringify(touch));
  // last-touch só atualiza quando há sinal novo (parâmetros ou referência externa)
  if (hasParams || (!sameHost && referrer)) safeSet(K_LAST, JSON.stringify(touch));
}

export function getAttribution(): Store {
  return {
    first: parse(safeGet(K_FIRST)),
    last: parse(safeGet(K_LAST)),
    sid: getSid(),
  };
}

/** Resumo humano da origem, ex.: "google / cpc / fibra-araranguá" ou "orgânico (google)". */
export function attributionSummary(): string {
  const { last, first } = getAttribution();
  const t = last ?? first;
  if (!t) return "direto";
  const p = t.params;
  if (p.utm_source || p.utm_medium || p.utm_campaign) {
    return [p.utm_source, p.utm_medium, p.utm_campaign].filter(Boolean).join(" / ");
  }
  if (p.gclid || p.gbraid || p.wbraid || p.gad_source) return "google / ads";
  if (p.fbclid) return "meta";
  if (p.msclkid) return "bing / ads";
  if (t.referrer) {
    try {
      return "referência: " + new URL(t.referrer).host.replace(/^www\./, "");
    } catch {
      return "referência externa";
    }
  }
  return "direto";
}

/** Objeto achatado para mandar ao GA4/Ads e ao Huggy. */
export function attributionParams(): Record<string, string> {
  const { last, first, sid } = getAttribution();
  const t = last ?? first;
  const out: Record<string, string> = { ci_sid: sid };
  if (t) {
    Object.assign(out, t.params);
    if (t.landing) out.landing_page = t.landing;
    if (t.referrer) out.referrer = t.referrer;
  }
  return out;
}

/**
 * Trecho anexado à mensagem do WhatsApp para o atendente ver a origem.
 * `ctx` é o ponto do site de onde a pessoa saiu (ex.: "planos", "hero").
 */
/** Página atual em texto legível (fallback de contexto pros botões sem ctx). */
function paginaAtual(): string {
  if (typeof window === "undefined") return "";
  const p = window.location.pathname.replace(/\/+$/, "");
  if (!p) return "home";
  const nomes: Record<string, string> = {
    "/planos": "planos de internet",
    "/telefonia": "telefonia",
    "/hospedagem": "hospedagem",
    "/empresas": "empresas",
    "/cameras": "câmeras",
    "/aplicativos": "aplicativos",
    "/lojas": "lojas",
    "/sobre": "quem somos",
    "/blog": "blog",
    "/trabalhe-conosco": "trabalhe conosco",
    "/area-do-cliente": "área do cliente",
  };
  return nomes[p] ?? p.replace(/^\//, "").replace(/\//g, " › ");
}

export function attributionRef(ctx?: string): string {
  const { sid } = getAttribution();
  const parts = ["site"];
  const onde = ctx || paginaAtual();
  if (onde) parts.push(onde);
  const origem = attributionSummary();
  if (origem && origem !== "direto") parts.push(origem);
  parts.push("#" + sid);
  return `\n\n— origem: ${parts.join(" · ")}`;
}
